import React from "react";
import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1713461983836-de0a45009424?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxmaW5hbmNpYWwlMjBvZmZpY2UlMjBkZXNrfGVufDB8fHx8MTc4MTU1OTYwMnww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 md:pt-40 pb-20 md:pb-28"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7 relative">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="overline mb-6"
          >
            <span className="divider-gold mr-3" />
            Chartered Accountants · Est. Chittoor
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.05 }}
            className="font-serif text-[#111814] text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight"
            data-testid="hero-headline"
          >
            Precise counsel <em className="italic text-[#1A2F24]">for</em>
            <br />
            careful businesses.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mt-8 max-w-xl text-[#3A453F] text-lg leading-relaxed"
            data-testid="hero-subtext"
          >
            Roopesh Kumar &amp; Associates is a practice of Chartered
            Accountants serving entrepreneurs, professionals and Business
            owners in Bengaluru, Chennai, Hyderabad and across India — with
            quiet diligence in tax, audit and compliance.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#contact"
              className="btn-primary"
              data-testid="hero-cta-primary"
            >
              Book a Consultation <ArrowDownRight size={16} />
            </a>
            <a
              href="#services"
              className="btn-secondary"
              data-testid="hero-cta-secondary"
            >
              Our Services
            </a>
          </motion.div>

          <div className="mt-16 grid grid-cols-3 gap-6 max-w-lg">
            {[
              { k: "1+", v: "Year of Practice" },
              { k: "50+", v: "Clients Served" },
              { k: "100%", v: "Compliance Record" },
            ].map((s) => (
              <div key={s.v} data-testid={`hero-stat-${s.k}`}>
                <div className="font-serif text-3xl md:text-4xl text-[#1A2F24]">
                  {s.k}
                </div>
                <div className="text-xs tracking-[0.14em] uppercase text-[#68766E] mt-2">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative">
            <img
              src={HERO_IMG}
              alt="Financial office desk with documents"
              className="w-full h-[460px] md:h-[560px] object-cover"
              data-testid="hero-image"
            />
            <div className="absolute -bottom-6 -left-6 bg-[#1A2F24] text-[#FDFBF7] p-6 md:p-8 max-w-[260px]">
              <div className="overline text-[#C5A059] mb-2">Ledger No. 01</div>
              <div className="font-serif text-2xl leading-tight">
                Filed with care. Audited with conscience.
              </div>
            </div>
            <div className="absolute -top-6 -right-6 w-24 h-24 border border-[#C5A059] hidden md:block" />
          </div>
        </motion.div>
      </div>

      {/* Marquee */}
      <div className="mt-24 md:mt-32 border-y border-[#E5E1D8] py-5 overflow-hidden bg-[#F5F1E8]/50">
        <div className="ticker-track">
          {Array.from({ length: 2 }).map((_, idx) => (
            <div
              key={idx}
              className="flex items-center gap-12 px-6 font-serif italic text-[#1A2F24] text-xl md:text-2xl whitespace-nowrap"
            >
              <span>Income Tax</span>
              <span className="text-[#C5A059]">✦</span>
              <span>GST &amp; Returns</span>
              <span className="text-[#C5A059]">✦</span>
              <span>Statutory Audit</span>
              <span className="text-[#C5A059]">✦</span>
              <span>ROC Compliance</span>
              <span className="text-[#C5A059]">✦</span>
              <span>Company Incorporation</span>
              <span className="text-[#C5A059]">✦</span>
              <span>Bookkeeping</span>
              <span className="text-[#C5A059]">✦</span>
              <span>Tax Advisory</span>
              <span className="text-[#C5A059]">✦</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
